import { Hammer, Hourglass } from 'lucide-react';
import { useContext } from 'react';
import SwitchRoleToggle from './SwitchRoleToggle';
import { UserDetailContext } from '../../../context/userDetails/UserDetailContext';
import { useLocation, useNavigate } from 'react-router-dom';

export default function UpgradeToArtisanButton({
  loggedIn,
  isDualRole,
  userDetail: user,
  handleUpgradeToArtisan,
}) {
  const { userDetail } = useContext(UserDetailContext);
  const isPending = userDetail?.approval_status === 'PENDING';
  const location = useLocation();
  const navigate = useNavigate();

  const openJoinArtisan = () => navigate('/join-artisan', { state: { background: location } });

  return (
    <>
      {/* DESKTOP – FULL BUTTON (Only on extra large screens) */}
      <div className="hidden xl:flex gap-1">
        {loggedIn ? (
          isDualRole ? (
            <SwitchRoleToggle />
          ) : user.role === 'user' ? (
            <button
              onClick={handleUpgradeToArtisan}
              className="
    relative flex items-center gap-3
    px-6 py-3 lg:px-4 lg:py-2
    bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500
    text-gray-900 font-semibold rounded-xl
    shadow-md hover:shadow-lg hover:scale-105
    transition-all duration-300 ease-out
    border border-yellow-300
    overflow-hidden
  "
            >
              <div className="flex items-center justify-center w-6 h-6 lg:w-7 lg:h-7 bg-white rounded-full shadow-sm">
                <Hammer className="w-4 h-4 text-yellow-500" strokeWidth={2.5} />
              </div>
              <span className="text-sm sm:text-base lg:text-base">
                Switch to Artisan
              </span>
              <span className="absolute top-0 left-0 w-full h-full bg-yellow-200 opacity-10 rounded-xl animate-pulse pointer-events-none"></span>
            </button>
          ) : (
            <div className="relative group">
              <div
                className="
                flex items-center gap-2
                px-4 py-2
                rounded-full
                bg-amber-50 border border-amber-200
                text-amber-700 text-sm font-medium
                shadow-sm
                hover:bg-amber-100 hover:border-amber-300
                transition cursor-default
              "
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white ${isPending ? 'bg-amber-400' : 'bg-amber-500'}`}
                >
                  {isPending ? (
                    <Hourglass className="w-3 h-3 sm:w-4 sm:h-4 animate-spin-slow" />
                  ) : (
                    <Hammer className="w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </span>
                <span className="text-sm sm:text-base">
                  {isPending ? 'Pending Artisan' : 'Artisan'}
                </span>
              </div>
              <div
                className="
                absolute top-full mt-2 left-1/2 -translate-x-1/2
                bg-gray-900 text-white text-xs
                px-3 py-1 rounded-md
                opacity-0 group-hover:opacity-100
                transition pointer-events-none
              "
              >
                {isPending
                  ? 'Verification in progress'
                  : 'Artisan account enabled'}
              </div>
            </div>
          )
        ) : (
          <button
            onClick={openJoinArtisan}
            className="
            px-6 py-3
            bg-gradient-to-r from-rose-500 to-rosy-500
            text-white font-semibold rounded-lg
            hover:shadow-lg hover:shadow-rose-500/50
            transition-all
            flex items-center gap-2.5
          "
          >
            <Hammer className="w-5 h-5 sm:w-6 sm:h-6 lg:w-6 lg:h-6" />
            <span className="text-sm sm:text-base lg:text-base">
              Join as Artisan
            </span>
          </button>
        )}
      </div>

      {/* TABLET & SMALL DESKTOP (md to xl) – ICON ONLY */}
      <div className="hidden md:flex xl:hidden gap-2">
        {loggedIn && user.role === 'user' && (
          <button
            onClick={handleUpgradeToArtisan}
            aria-label="Switch to Artisan"
            className="
            p-3 rounded-xl
            bg-amber-100 border border-amber-300
            text-amber-700
            hover:bg-amber-200
            transition
          "
          >
            <Hammer className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}

        {!loggedIn && (
          <button
            onClick={openJoinArtisan}
            aria-label="Join as Artisan"
            className="
            p-3 rounded-xl
            bg-rose-500 text-white
            hover:bg-rose-600
            transition
          "
          >
            <Hammer className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}
      </div>
    </>
  );
}
